import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { showToast } from "@/providers";

interface MockModeContextData {
  isMockMode: boolean;
  toggleMockMode: () => void;
  isLoading: boolean;
  isMswReady: boolean;
}

const MockModeContext = createContext<MockModeContextData | undefined>(undefined);

const MOCK_MODE_KEY = "vogue-mock-mode";

// Detectar se está rodando no Lovable
const isLovable = window.location.hostname.includes("lovable.app") || window.location.hostname.includes("lovable.dev");

export function MockModeProvider({ children }: { children: React.ReactNode }) {
  const [isLoading, setIsLoading] = useState(true);
  const [isMswReady, setIsMswReady] = useState(false);
  const [isMockMode, setIsMockMode] = useState(() => {
    // Só habilitar mock mode em desenvolvimento ou no Lovable
    if (!import.meta.env.DEV && !isLovable) {
      return false;
    }

    // Verificar localStorage
    const stored = localStorage.getItem(MOCK_MODE_KEY);
    if (stored !== null) {
      return stored === "true";
    }

    // Default: mock no Lovable, real em desenvolvimento local
    return isLovable;
  });

  // Função para inicializar/parar MSW
  const configureMSW = useCallback(async (enable: boolean) => {
    if (!import.meta.env.DEV && !isLovable) {
      return;
    }

    setIsLoading(true);
    setIsMswReady(false);

    try {
      if (enable) {
        console.log("🔧 Iniciando MSW...");
        const { worker } = await import("@/mocks/browser");
        console.log("🔧 Worker importado:", worker);

        await worker.start({
          onUnhandledRequest: "bypass",
          serviceWorker: {
            url: "/mockServiceWorker.js",
          },
        });
        console.log("🎭 MSW Mock Mode: ATIVADO");

        // Verificar se os handlers estão registrados
        console.log("🔧 Handlers registrados:", worker.listHandlers().length);

        // MSW está pronto
        setIsMswReady(true);
      } else {
        const { worker } = await import("@/mocks/browser");
        worker.stop();
        console.log("🌐 MSW Mock Mode: DESATIVADO (usando API real)");
        setIsMswReady(false);
      }
    } catch (error) {
      console.error("❌ Erro ao configurar MSW:", error);
      showToast({ type: "error", title: "Erro ao alternar modo mock" });
      setIsMswReady(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Inicializar MSW na montagem se necessário
  useEffect(() => {
    if ((import.meta.env.DEV || isLovable) && isMockMode) {
      configureMSW(true);
    } else {
      setIsLoading(false);
      // Em modo real, MSW não é necessário
      setIsMswReady(true);
    }
  }, []); // Executar apenas na montagem inicial

  const toggleMockMode = useCallback(async () => {
    if (!import.meta.env.DEV && !isLovable) {
      return;
    }

    const newMode = !isMockMode;

    // Atualizar estado e localStorage
    setIsMockMode(newMode);
    localStorage.setItem(MOCK_MODE_KEY, String(newMode));

    // Configurar MSW
    await configureMSW(newMode);

    // Feedback visual
    showToast({
      type: "success",
      title: newMode ? "🎭 Modo Mock ativado" : "🌐 Modo Real ativado (API)",
      autoClose: 2000,
    });
  }, [isMockMode, configureMSW]);

  return (
    <MockModeContext.Provider value={{ isMockMode, toggleMockMode, isLoading, isMswReady }}>
      {children}
    </MockModeContext.Provider>
  );
}

export function useMockMode() {
  const context = useContext(MockModeContext);
  if (context === undefined) {
    throw new Error("useMockMode deve ser usado dentro de MockModeProvider");
  }
  return context;
}
